import { DynamicConfig } from '@statsig/js-client';
import { Injectable } from '@angular/core';

import { Session } from '@services/session';
import { StatsigService } from '@services/statsig.service';

import { IInsuranceUpsellDynamicConfig } from '@misc/upsell/upsell-config';

// define an enum if feature gate key is used anywhere else in the app
export enum FLEET_GROWTH_FEATURE_FLAGS {
  FG_DASHBOARD_UPSELLS = 'fg-dashboard-upsells',
  FG_ENABLE_TAB_TITLES = 'fg-enable-tab-titles',
  FG_EOY_UPSELL_ENABLED = 'fg-eoy-upsell-enabled',
  FG_EOQ_UPSELL_Q1_2023 = 'fg-eoq-upsell-q1-2023',
  FG_UPDATE_CREDIT_CARD = 'fg-update-credit-card',
  FG_BILLING_MANAGEMENT = 'fg-billing-management',
  FG_ADA_CHATBOT_ENABLED = 'fg-ada-chatbot-enabled',
  FG_UPSELL_PENDO_GUIDES = 'fg-upsell-pendo-guides',
  FG_ENABLE_ES_MX_LOCALE = 'fg-enable-es-mx-locale',
  FG_EARN_CASH_TAB_ENABLED = 'fg-earn-cash-tab-enabled', // deprecated
  FG_CHATBOT_SINGLE_CLICK_UX = 'fg-chatbot-single-click-ux',
  FG_CONTACT_SALES_EXPERIMENT = 'fg-contact-sales-experiment',
  FG_MARKETPLACE_EXPANSION = 'fg-marketplace-expansion-enabled',
  FG_ADD_DEVICES_HARDWARE_UPSELL = 'fg-add-devices-hardware-upsell',
  FG_PRODUCT_FEEDBACK_SUPPORT_MENU = 'fg-product-feedback-support-menu',
  FG_REQUEST_CANCELLATION_EXPERIMENT = 'fg-request-cancellation-experiment',
  FG_BILLING_MANAGEMENT_VALIDATE_API_ELIGIBILITY = 'fg-billing-management-validate-eligibility',
  FG_DATE_FORMATTING_LOCALIZATION = 'date-formatting-localization',
  FG_VSB_AND_LOWER_SMB_TO_SMB = 'fg-vsb-lower-smb-to-smb',
  FG_COMPANY_WISE_SHOP_PERMISSION = 'company-wise-shop-permission',
  FG_SFDC_REARCHITECTURE = 'fg-sfdc-rearchitecture',
  FG_GEOFENCE_RISK_ZONE = 'fg-geofence-risk-zone',
  FG_LOCALISE_ADDRESS_FORMS = 'fg-localise-address-form',
}

export enum FLEET_GROWTH_DYNAMIC_CONFIGS {
  FG_UPSELL_ENTRY_POPUP = 'fg-upsell-entry-popup',
  INSURANCE_UPSELL_EXPERIMENT = 'insurance-upsell-experiment',
}

const ENTERPRISE_MIN_FLEET_SIZE = 500;

@Injectable({ providedIn: 'root' })
export class FleetGrowthFeatureFlagService {

  constructor(private session: Session, private statsigService: StatsigService) { }

  public isDashboardUpsellsEnabled(): boolean {
    return this.statsigService.evaluateGate(FLEET_GROWTH_FEATURE_FLAGS.FG_DASHBOARD_UPSELLS);
  }

  public isProductFeedbackMenuEnabled(): boolean {
    return this.statsigService
      .evaluateGate(FLEET_GROWTH_FEATURE_FLAGS.FG_PRODUCT_FEEDBACK_SUPPORT_MENU);
  }

  public getUpsellEntryPopupVariation(): string {
    const upsellConfig: DynamicConfig =
      this.statsigService.evaluateDynamicConfig(FLEET_GROWTH_DYNAMIC_CONFIGS.FG_UPSELL_ENTRY_POPUP);
    let upsellValue: string = !!upsellConfig ? upsellConfig.value['value'] as string : '';

    if (upsellValue === 'get_demo_ai_dashcam' && this.isEligibleForInsuranceUpsell()) {
      upsellValue = '';
    }

    return upsellValue;
  }

  public isEOQUpsellQ12023Enabled(): boolean {
    return this.statsigService
      .evaluateGate(FLEET_GROWTH_FEATURE_FLAGS.FG_EOQ_UPSELL_Q1_2023);
  }
  public isEOYUpsellEnabled(): boolean {
    return this.statsigService
      .evaluateGate(FLEET_GROWTH_FEATURE_FLAGS.FG_EOY_UPSELL_ENABLED);
  }

  public isDateFormattingLocalizationEnabled(): boolean {
    return this.statsigService
      .evaluateGate(FLEET_GROWTH_FEATURE_FLAGS.FG_DATE_FORMATTING_LOCALIZATION);
  }

  public isVSBAndLowerSMBToSMBEnabled(): boolean {
    return this.statsigService
      .evaluateGate(FLEET_GROWTH_FEATURE_FLAGS.FG_VSB_AND_LOWER_SMB_TO_SMB);
  }

  public isCompanyWiseShopPermissionEnabled(): boolean {
    return this.statsigService.evaluateGate(FLEET_GROWTH_FEATURE_FLAGS.FG_COMPANY_WISE_SHOP_PERMISSION);
  }

  public isSFDCRearchitectureEnabled(): boolean {
    return this.statsigService.evaluateGate(FLEET_GROWTH_FEATURE_FLAGS.FG_SFDC_REARCHITECTURE);
  }

  // deprecated
  public isEarnCashTabEnabled(): boolean {
    return this.statsigService
      .evaluateGate(FLEET_GROWTH_FEATURE_FLAGS.FG_EARN_CASH_TAB_ENABLED);
  }

  public isChatbotEnabled(): boolean {
    return this.statsigService.evaluateGate(FLEET_GROWTH_FEATURE_FLAGS.FG_ADA_CHATBOT_ENABLED);
  }

  public isMarketplaceExpansionEnabled(): boolean {
    return this.statsigService.evaluateGate(FLEET_GROWTH_FEATURE_FLAGS.FG_MARKETPLACE_EXPANSION);
  }

  public isHardwareDevicesUpsellEnabled(): boolean {
    return this.statsigService.evaluateGate(FLEET_GROWTH_FEATURE_FLAGS.FG_ADD_DEVICES_HARDWARE_UPSELL);
  }

  public isPendoUpsellGuidesEnabled(): boolean {
    return this.statsigService.evaluateGate(FLEET_GROWTH_FEATURE_FLAGS.FG_UPSELL_PENDO_GUIDES);
  }

  public isUpdateCreditCardEnabled(): boolean {
    return this.statsigService.evaluateGate(FLEET_GROWTH_FEATURE_FLAGS.FG_UPDATE_CREDIT_CARD);
  }

  public isBillingManagementEnabled(): boolean {
    return this.statsigService.evaluateGate(FLEET_GROWTH_FEATURE_FLAGS.FG_BILLING_MANAGEMENT);
  }

  public isDashcamUpsellEnabled(): boolean {
    return this.isAGVGUpsellEnabled() && !this.session.hasCompanyVideoMonitoringEnabled();
  }

  public isContactSalesExperimentEnabled(): boolean {
    return this.statsigService.evaluateGate(FLEET_GROWTH_FEATURE_FLAGS.FG_CONTACT_SALES_EXPERIMENT);
  }

  public isAGVGUpsellEnabled(): boolean {
    return this.isDashboardUpsellsEnabled() && (this.session.isAdmin() || this.session.isDispatcher());
  }
  // deprecated
  public isEarnCashTabDisabled(): boolean {
    /*
    *  if "Earn Cash" ff is false, then hide "Earn Cash" menu item
    *  else, decide based on the company property
    */

    const isEnterpriseCustomer: boolean = this.session.getCompanyProperty('composite_fleet_size') > ENTERPRISE_MIN_FLEET_SIZE;

    if (!this.isEarnCashTabEnabled()) {
      return true;
    } else {
      return isEnterpriseCustomer ? true : this.session.getCompanyProperty('earn_cash_tab_disabled');
    }
  }

  public isCreditCardFeatureFlagEnabled(): boolean {
    return (this.isUpdateCreditCardEnabled() && this.session.isAdmin());
  }

  public isDispatchDashboardNavItemEnabled(): boolean {
    return (this.session.isDispatchDriverWorkflowEnabled() &&
      this.session.isDispatcher() && this.session.isDispatchReadEnabled());
  }

  public isAdminBillingManagementEnabled(): boolean {
    // common permissions
    const isEligible: boolean = this.session.userPermissions.isBillingManagementManageAllowed()
    && this.isBillingManagementEnabled()

    return isEligible;
  }

  public isChatbotSingleClickUXEnabled(): boolean {
    return this.statsigService.evaluateGate(FLEET_GROWTH_FEATURE_FLAGS.FG_CHATBOT_SINGLE_CLICK_UX);
  }

  public isTabTitlesEnabled(): boolean {
    return this.statsigService.evaluateGate(FLEET_GROWTH_FEATURE_FLAGS.FG_ENABLE_TAB_TITLES);
  }

  public isEligibleForInsuranceUpsell(): boolean {
    let isEligibleForInsuranceUpsell: boolean = true;
    let insuranceUpsellGuidesConfigs: IInsuranceUpsellDynamicConfig[];

    const allAvailablePendoGuides: any = window['pendo'].guides;
    const insuranceUpsellDynamicConfig: DynamicConfig =
      this.statsigService.evaluateDynamicConfig(FLEET_GROWTH_DYNAMIC_CONFIGS.INSURANCE_UPSELL_EXPERIMENT);

    if (!allAvailablePendoGuides) return isEligibleForInsuranceUpsell;

    if (insuranceUpsellDynamicConfig) {
      insuranceUpsellGuidesConfigs =
      !!insuranceUpsellDynamicConfig.value['guidesData'] ?
      insuranceUpsellDynamicConfig.value['guidesData'] as IInsuranceUpsellDynamicConfig[] :
      [];
    }

    if (!!insuranceUpsellGuidesConfigs && insuranceUpsellGuidesConfigs.length > 0) {
      const allInsuranceUpsellGuidesIds: string[] = insuranceUpsellGuidesConfigs.map(guide => guide.guideId);
      const insuranceUpsellGuides: any = allAvailablePendoGuides.filter(guide => allInsuranceUpsellGuidesIds.includes(guide.id));
      isEligibleForInsuranceUpsell = insuranceUpsellGuides.length > 0;
    }

    return isEligibleForInsuranceUpsell;
  }

  public isRequestCancellationExperimentEnabled(): boolean {
    return this.statsigService.evaluateGate(FLEET_GROWTH_FEATURE_FLAGS.FG_REQUEST_CANCELLATION_EXPERIMENT);
  }

  public isGeofenceRiskZoneEnabled(): boolean {
    return this.statsigService.evaluateGate(FLEET_GROWTH_FEATURE_FLAGS.FG_GEOFENCE_RISK_ZONE);
  }

  public isLocaliseAddressFormEnabled(): boolean {
    return this.statsigService.evaluateGate(FLEET_GROWTH_FEATURE_FLAGS.FG_LOCALISE_ADDRESS_FORMS);
  }
}
